<template>
  <div>
    <header>
      <h3 class="float-left">廣告刊登申請</h3>
    </header>
    <v-container>
      <v-row justify="center">
        <v-col cols="12" md="8" lg="7">
          <v-alert outlined type="info" color="primary">
            廣告送出後會先由後台審核；審核通過後會寄送繳費方式，付款完成後立即上線。
          </v-alert>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col cols="12" md="8" lg="7">
          <v-form ref="form" v-model="valid" @submit.prevent="submit">
            <v-card>
              <v-card-title>廣告資料</v-card-title>
              <v-card-text>
                <v-select
                  v-model="form.slot"
                  :items="slotItems"
                  label="選擇版位"
                  outlined
                  :rules="[required]"
                ></v-select>
                <v-alert dense outlined type="info" color="primary">
                  建議圖片尺寸：{{ selectedSlotSpec.size }}，比例
                  {{ selectedSlotSpec.ratio }}。{{ selectedSlotSpec.note }}
                </v-alert>
                <v-select
                  v-model="form.requestedDays"
                  :items="dayItems"
                  label="投放方案"
                  outlined
                  :rules="[required]"
                ></v-select>
                <div class="mb-4 price-preview">
                  預計金額：NT$ {{ suggestedPrice.toLocaleString() }}
                </div>
                <v-text-field
                  v-model="form.title"
                  label="廣告標題"
                  outlined
                  :rules="[required]"
                ></v-text-field>
                <v-text-field
                  v-model="form.url"
                  label="廣告連結"
                  outlined
                  placeholder="https://"
                  :rules="[required, urlRule]"
                ></v-text-field>
                <input
                  ref="imageInput"
                  class="d-none"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  @change="selectImage"
                />
                <div
                  class="upload-zone mb-2"
                  :class="{ dragging: dragging }"
                  @click="$refs.imageInput.click()"
                  @dragover.prevent="dragging = true"
                  @dragleave.prevent="dragging = false"
                  @drop.prevent="dropImage"
                >
                  <v-icon class="mb-2" color="primary" size="42">
                    mdi-image-plus
                  </v-icon>
                  <div class="upload-title">拖拉圖片到這裡，或點擊選擇檔案</div>
                  <div class="upload-hint">
                    請上傳 {{ selectedSlotSpec.size }}，JPG / PNG / WebP，建議
                    500KB 以內
                  </div>
                  <div v-if="imageFile" class="upload-file">
                    {{ imageFile.name }}
                  </div>
                </div>
                <div v-if="!form.imageDataUrl" class="upload-error mb-4">
                  請上傳圖片
                </div>
                <v-img
                  v-if="form.imageDataUrl"
                  :src="form.imageDataUrl"
                  class="mb-4 preview"
                  contain
                ></v-img>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-title>聯絡資訊</v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="form.contactName"
                  label="* 聯絡人"
                  outlined
                  :rules="[required]"
                ></v-text-field>
                <v-text-field
                  v-model="form.contactEmail"
                  label="* Email"
                  outlined
                  :rules="[required, emailRule]"
                ></v-text-field>
                <v-text-field
                  v-model="form.company"
                  label="公司/店名"
                  outlined
                ></v-text-field>

                <v-text-field
                  v-model="form.contactPhone"
                  label="電話"
                  outlined
                ></v-text-field>
                <v-textarea
                  v-model="form.note"
                  label="備註"
                  outlined
                  rows="3"
                ></v-textarea>
              </v-card-text>
              <v-card-actions class="px-4 pb-4">
                <v-spacer></v-spacer>
                <v-btn color="primary" large type="submit" :loading="loading">
                  送出申請
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/libs/config.js";

export default {
  name: "AdsApply",
  data: () => ({
    valid: false,
    loading: false,
    dragging: false,
    imageFile: null,
    form: {
      slot: "top",
      requestedDays: 7,
      title: "",
      url: "",
      imageDataUrl: "",
      contactName: "",
      company: "",
      contactEmail: "",
      contactPhone: "",
      note: "",
    },
    slotItems: [
      { text: "首頁主要廣告區 (位於首頁正上方)", value: "top" },
      { text: "浮動廣告區 (浮動首頁右下角)", value: "floating" },
    ],
    plans: {
      top: { 7: 400, 14: 800, 30: 1500 },
      floating: { 7: 400, 14: 800, 30: 1500 },
    },
    slotSpecs: {
      top: {
        size: "1200 x 300",
        ratio: "4:1",
        note: "首頁上方橫幅，手機會等比縮放。",
      },
      floating: {
        size: "700 x 500",
        ratio: "7:5",
        note: "浮動廣告區，適合商品照、標題與價格。",
      },
    },
  }),
  computed: {
    selectedSlotSpec() {
      return this.slotSpecs[this.form.slot] || this.slotSpecs.top;
    },
    dayItems() {
      const plan = this.plans[this.form.slot] || {};
      return [7, 14, 30].map((days) => ({
        text: `${days} 天 / NT$ ${(plan[days] || 0).toLocaleString()}`,
        value: days,
      }));
    },
    suggestedPrice() {
      return this.plans[this.form.slot]?.[this.form.requestedDays] || 0;
    },
  },
  methods: {
    required(value) {
      return !!value || "必填";
    },
    urlRule(value) {
      return /^https?:\/\//.test(value || "") || "請填寫 http 或 https 連結";
    },
    emailRule(value) {
      return /.+@.+\..+/.test(value || "") || "Email 格式不正確";
    },
    fileRule() {
      return !!this.form.imageDataUrl || "請上傳圖片";
    },
    selectImage(event) {
      const file = event.target.files?.[0];
      this.readImage(file);
    },
    dropImage(event) {
      this.dragging = false;
      const file = event.dataTransfer.files?.[0];
      this.readImage(file);
    },
    readImage(file) {
      this.form.imageDataUrl = "";
      if (!file) return;
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        this.$toast.error("圖片格式需為 JPG / PNG / WebP");
        this.imageFile = null;
        return;
      }
      if (file.size > 1.5 * 1024 * 1024) {
        this.$toast.error("圖片請控制在 1.5MB 以內");
        this.imageFile = null;
        return;
      }
      this.imageFile = file;
      const reader = new FileReader();
      reader.onload = (event) => {
        this.form.imageDataUrl = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        await axios.post(`${config.API_URL}/ads/applications`, {
          ...this.form,
          suggestedPrice: this.suggestedPrice,
        });
        this.$toast.success("申請已送出，審核通過後會寄送繳費方式。");
        this.$refs.form.reset();
        this.form.imageDataUrl = "";
        this.imageFile = null;
        if (this.$refs.imageInput) {
          this.$refs.imageInput.value = "";
        }
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.$toast.error(errors ? errors.join("、") : "送出失敗，請稍後再試");
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
header {
  padding: 0 20px;
  height: 50px;
  line-height: 50px;
  background-color: #1976d2;
  color: #fff;
}
.preview {
  min-height: 160px;
  max-height: 320px;
  background: #f5f5f5;
}
.upload-zone {
  align-items: center;
  border: 2px dashed #90caf9;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 170px;
  padding: 22px;
  text-align: center;
  transition: background-color 0.2s, border-color 0.2s;
}
.upload-zone.dragging {
  background: #e3f2fd;
  border-color: #1976d2;
}
.upload-title {
  font-size: 17px;
  font-weight: 700;
}
.upload-hint {
  color: #666;
  font-size: 14px;
  margin-top: 6px;
}
.upload-file {
  color: #1976d2;
  font-weight: 700;
  margin-top: 10px;
}
.upload-error {
  color: #ff5252;
  font-size: 12px;
}
.price-preview {
  color: #1976d2;
  font-size: 18px;
  font-weight: 700;
}
</style>
